import React, { useState, useEffect } from 'react';
import { getMe, updateMe, removeContactMethod } from '../services/users';
import { submitPermissionRequest, getMyPendingRequests } from '../services/permissionRequests';
import { getTeacherProfile, updateTeacherProfile } from '../services/teachers';
import { User, NotificationPref, PermissionRequest, RequestableRole, TeacherProfile } from '../types/models';
import { Link } from 'react-router-dom';
import { useLocations } from '../hooks/useLocations';
import { useAuth } from '../contexts/AuthContext';
import ContactVerificationModal from '../components/ContactVerificationModal';

export const ProfilePage: React.FC = () => {
  const { refreshUser } = useAuth();
  const { locations } = useLocations();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [defaultLocation, setDefaultLocation] = useState('');
  const [notifPref, setNotifPref] = useState<NotificationPref>('email');
  const [optIn, setOptIn] = useState(true);

  // Contact methods
  const [contactEditType, setContactEditType] = useState<'email' | 'phone' | null>(null);
  const [contactInputValue, setContactInputValue] = useState('');
  const [contactInputError, setContactInputError] = useState<string | null>(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [pendingVerifyType, setPendingVerifyType] = useState<'email' | 'phone' | null>(null);
  const [pendingVerifyValue, setPendingVerifyValue] = useState('');
  const [removing, setRemoving] = useState<'phone' | 'email' | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [showPhoneNudge, setShowPhoneNudge] = useState(false);

  const [pendingRequests, setPendingRequests] = useState<PermissionRequest[]>([]);
  const [reqTargetRole, setReqTargetRole] = useState<RequestableRole>('Teacher');
  const [reqReason, setReqReason] = useState('');
  const [reqYaUrl, setReqYaUrl] = useState('');
  const [submittingReq, setSubmittingReq] = useState(false);
  const [reqMessage, setReqMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const [teacherProfile, setTeacherProfile] = useState<TeacherProfile | null>(null);
  const [ldrBio, setLdrBio] = useState('');
  const [ldrYears, setLdrYears] = useState('');
  const [ldrSpecialties, setLdrSpecialties] = useState<string[]>([]);
  const [ldrCerts, setLdrCerts] = useState<string[]>([]);
  const [ldrYaUrl, setLdrYaUrl] = useState<string | null>(null);
  const [ldrSaving, setLdrSaving] = useState(false);
  const [ldrMessage, setLdrMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const loadUser = () =>
    getMe().then(u => {
      setUser(u);
      setName(u.name);
      setUsername(u.username ?? '');
      setDefaultLocation(u.defaultLocations?.[0] ?? '');
      setNotifPref(u.notificationPreference);
      setOptIn(u.optInNewEventNotifications);
      if (u.roles.includes('Teacher')) {
        getTeacherProfile(u.username ?? u.userId)
          .then(lp => {
            setTeacherProfile(lp);
            setLdrBio(lp.bio ?? '');
            setLdrYears(lp.yearsExperience?.toString() ?? '');
            setLdrSpecialties(lp.specialties ?? []);
            setLdrCerts(lp.certifications ?? []);
            setLdrYaUrl(null);
          })
          .catch(() => {});
      }
    });

  useEffect(() => {
    loadUser()
      .catch(err => setError((err as Error).message))
      .finally(() => setLoading(false));
    getMyPendingRequests().then(setPendingRequests).catch(() => {});
  }, []);

  const isEligibleToRequest = (u: User) =>
    !u.roles.includes('Admin') &&
    !u.roles.includes('Teacher') &&
    !u.roles.includes('Organizer');

  // ── Contact methods ────────────────────────────────────────────────────────

  const openContactEdit = (type: 'email' | 'phone') => {
    if (contactEditType !== null) return; // one change at a time
    setContactEditType(type);
    setContactInputValue('');
    setContactInputError(null);
    setRemoveError(null);
  };

  const cancelContactEdit = () => {
    setContactEditType(null);
    setContactInputValue('');
    setContactInputError(null);
  };

  const handleContactSend = () => {
    const v = contactInputValue.trim();
    if (contactEditType === 'email') {
      if (!v.includes('@') || !v.includes('.')) {
        setContactInputError('Enter a valid email address.');
        return;
      }
    }
    if (contactEditType === 'phone') {
      if (!/^\+[1-9]\d{7,14}$/.test(v)) {
        setContactInputError('Enter phone in E.164 format, e.g. +15551234567.');
        return;
      }
    }
    setContactInputError(null);
    setPendingVerifyType(contactEditType);
    setPendingVerifyValue(v);
    setContactEditType(null);
    setShowVerifyModal(true);
  };

  const handleVerified = async () => {
    setShowVerifyModal(false);
    const wasPhone = pendingVerifyType === 'phone';
    await loadUser().catch(() => {});
    if (wasPhone) {
      const u = await getMe().catch(() => null);
      if (u && (u.notificationPreference === 'app' || u.notificationPreference === 'email')) {
        setShowPhoneNudge(true);
      }
    }
    refreshUser();
  };

  const handleRemove = async (type: 'phone' | 'email') => {
    setRemoving(type);
    setRemoveError(null);
    try {
      await removeContactMethod(type);
      await loadUser();
      if (type === 'email') refreshUser();
    } catch (err) {
      setRemoveError((err as Error).message);
    } finally {
      setRemoving(null);
    }
  };

  const handleAcceptNudge = async () => {
    setShowPhoneNudge(false);
    try {
      await updateMe({ notificationPreference: 'sms' });
      setNotifPref('sms');
      setUser(prev => prev ? { ...prev, notificationPreference: 'sms' } : prev);
    } catch { /* ignore */ }
  };

  // ── Permission request ────────────────────────────────────────────────────

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reqReason.trim().length < 10) {
      setReqMessage({ text: 'Please provide at least 10 characters of context.', ok: false });
      return;
    }
    if (reqTargetRole === 'Teacher' && !reqYaUrl.trim()) {
      setReqMessage({ text: 'A Yoga Alliance profile URL is required for Teacher requests.', ok: false });
      return;
    }
    setSubmittingReq(true);
    setReqMessage(null);
    try {
      await submitPermissionRequest(
        reqTargetRole,
        reqReason.trim(),
        reqTargetRole === 'Teacher' ? reqYaUrl.trim() : undefined,
      );
      setReqMessage({ text: `Your request for ${reqTargetRole} has been submitted.`, ok: true });
      setReqReason('');
      setReqYaUrl('');
      const updated = await getMyPendingRequests();
      setPendingRequests(updated);
    } catch (err) {
      setReqMessage({ text: (err as Error).message, ok: false });
    } finally {
      setSubmittingReq(false);
    }
  };

  // ── Profile form ──────────────────────────────────────────────────────────

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!defaultLocation) {
      setError('Default location is required.');
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    setUsernameError(null);
    try {
      await updateMe({ name, username: username || undefined, defaultLocations: [defaultLocation], notificationPreference: notifPref, optInNewEventNotifications: optIn });
      await refreshUser();
      setMessage('Profile saved.');
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.includes('already taken')) {
        setUsernameError('That username is already taken.');
      } else if (msg.includes('Username must be')) {
        setUsernameError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Teacher profile ───────────────────────────────────────────────────────

  const SPECIALTIES = ['Vinyasa', 'Hatha', 'Restorative', 'Yin', 'Power', 'Ashtanga', 'SUP Yoga', 'Other'];
  const CERTIFICATIONS = ['RYT 200', 'RYT 500', 'E-RYT 200', 'E-RYT 500', 'RCYT', 'RPYT', 'Other'];

  const toggleItem = (list: string[], setList: (v: string[]) => void, item: string) => {
    setList(list.includes(item) ? list.filter(i => i !== item) : [...list, item]);
  };

  const handleTeacherSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setLdrSaving(true);
    setLdrMessage(null);
    try {
      await updateTeacherProfile(user.userId, {
        bio: ldrBio || undefined,
        yearsExperience: ldrYears ? parseInt(ldrYears, 10) : undefined,
        specialties: ldrSpecialties.length > 0 ? ldrSpecialties : undefined,
        certifications: ldrCerts.length > 0 ? ldrCerts : undefined,
        yogaAllianceUrl: ldrYaUrl === null ? undefined : (ldrYaUrl || null),
      });
      setLdrMessage({ text: 'Teacher profile saved.', ok: true });
      if (user) {
        getTeacherProfile(user.username ?? user.userId).then(lp => {
          setTeacherProfile(lp);
          setLdrYaUrl(null);
        }).catch(() => {});
      }
    } catch (err) {
      setLdrMessage({ text: (err as Error).message, ok: false });
    } finally {
      setLdrSaving(false);
    }
  };

  const verificationLabel = (status: TeacherProfile['verificationStatus']) => {
    if (status === 'verified') return { text: 'Verified ✓', cls: 'text-green-600' };
    if (status === 'provisional') return { text: 'Pending Verification', cls: 'text-yellow-600' };
    if (status === 'rejected') return { text: 'Rejected — contact admin', cls: 'text-red-600' };
    return { text: 'Unverified', cls: 'text-gray-500' };
  };

  const input = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500';
  const label = 'block text-sm font-medium text-gray-700 mb-1';

  if (loading) return <div className="text-center py-20 text-gray-500">Loading…</div>;

  return (
    <div className="max-w-xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Your Profile</h1>

      {/* Account summary */}
      {user && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Roles</dt>
              <dd className="text-gray-900">{user.roles.join(', ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Member since</dt>
              <dd className="text-gray-900">{new Date(user.createdAt).toLocaleDateString()}</dd>
            </div>
          </dl>
        </div>
      )}

      {/* Contact methods */}
      {user && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Contact Methods</h2>

          {showPhoneNudge && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
              Phone added! Switch to SMS notifications to receive class updates by text.{' '}
              <button onClick={handleAcceptNudge} className="font-medium underline">Enable SMS</button>
              {' · '}
              <button onClick={() => setShowPhoneNudge(false)} className="text-green-700 underline">Dismiss</button>
            </div>
          )}

          {removeError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-300 text-red-700 rounded-lg text-sm">{removeError}</div>
          )}

          {/* Email row */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Email</p>
              <p className="text-sm text-gray-900">{user.email ?? <span className="text-gray-400 italic">not set</span>}</p>
            </div>
            <div className="flex gap-2 ml-4 shrink-0">
              <button
                onClick={() => openContactEdit('email')}
                disabled={contactEditType !== null}
                className="text-xs text-green-600 hover:text-green-700 font-medium disabled:opacity-40"
              >
                Edit
              </button>
              {user.email && user.phoneNumber && (
                <button
                  onClick={() => handleRemove('email')}
                  disabled={removing !== null || contactEditType !== null}
                  className="text-xs text-red-500 hover:text-red-600 font-medium disabled:opacity-40"
                >
                  {removing === 'email' ? 'Removing…' : 'Remove'}
                </button>
              )}
            </div>
          </div>

          {/* Phone row */}
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Phone</p>
              <p className="text-sm text-gray-900">{user.phoneNumber ?? <span className="text-gray-400 italic">not set</span>}</p>
              {!user.phoneNumber && (
                <p className="text-xs text-gray-400 mt-0.5">Add a phone to enable SMS notifications.</p>
              )}
            </div>
            <div className="flex gap-2 ml-4 shrink-0">
              <button
                onClick={() => openContactEdit('phone')}
                disabled={contactEditType !== null}
                className="text-xs text-green-600 hover:text-green-700 font-medium disabled:opacity-40"
              >
                {user.phoneNumber ? 'Edit' : 'Add'}
              </button>
              {user.phoneNumber && user.email && (
                <button
                  onClick={() => handleRemove('phone')}
                  disabled={removing !== null || contactEditType !== null}
                  className="text-xs text-red-500 hover:text-red-600 font-medium disabled:opacity-40"
                >
                  {removing === 'phone' ? 'Removing…' : 'Remove'}
                </button>
              )}
            </div>
          </div>

          {/* Inline edit input */}
          {contactEditType && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {contactEditType === 'email' ? 'New email address' : 'New phone number'}
              </p>
              {contactEditType === 'phone' && (
                <p className="text-xs text-gray-400 mb-2">Enter in E.164 format, e.g. +15551234567</p>
              )}
              <div className="flex gap-2">
                <input
                  type={contactEditType === 'email' ? 'email' : 'tel'}
                  value={contactInputValue}
                  onChange={e => { setContactInputValue(e.target.value); setContactInputError(null); }}
                  placeholder={contactEditType === 'email' ? 'new@email.com' : '+15551234567'}
                  autoFocus
                  className={`flex-1 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${contactInputError ? 'border-red-400' : 'border-gray-300'}`}
                />
                <button
                  onClick={handleContactSend}
                  className="px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                >
                  Send Code
                </button>
                <button
                  onClick={cancelContactEdit}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
                >
                  Cancel
                </button>
              </div>
              {contactInputError && <p className="text-xs text-red-500 mt-1">{contactInputError}</p>}
            </div>
          )}

          {!user.email && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
              Phone-only accounts cannot currently reset their password via SMS. Keep your password safe — recovery requires contacting support.
            </p>
          )}
        </div>
      )}

      {/* Profile form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div>
          <label className={label}>Display Name</label>
          <input className={input} required value={name} onChange={e => setName(e.target.value)} />
        </div>

        <div>
          <label className={label}>Username</label>
          <input
            className={`${input} ${usernameError ? 'border-red-400' : ''}`}
            value={username}
            onChange={e => { setUsername(e.target.value.toLowerCase()); setUsernameError(null); }}
            placeholder="e.g. laura-stevens"
            autoComplete="off"
          />
          {usernameError ? (
            <p className="text-xs text-red-500 mt-1">{usernameError}</p>
          ) : (
            <p className="text-xs text-gray-400 mt-1">
              3–30 characters · lowercase letters, numbers, hyphens
              {user?.roles.includes('Teacher') && username && (
                <span className="ml-1 text-gray-500">· popupyoga.org/teachers/{username}</span>
              )}
            </p>
          )}
        </div>

        <div>
          <label className={label}>Default Location</label>
          <select
            className={input}
            required
            value={defaultLocation}
            onChange={e => setDefaultLocation(e.target.value)}
          >
            {!defaultLocation && <option value="">Select a location…</option>}
            {locations.map(loc => (
              <option key={loc.locationId} value={loc.locationId}>{loc.displayName}</option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">Used to pre-select events in your area.</p>
        </div>

        <div>
          <label className={label}>Notification Preference</label>
          <select className={input} value={notifPref} onChange={e => setNotifPref(e.target.value as NotificationPref)}>
            <option value="app">In-app only</option>
            <option value="email">Email &amp; in-app</option>
            <option value="sms">SMS &amp; in-app</option>
            <option value="both">Email, SMS &amp; in-app</option>
          </select>
          {(notifPref === 'sms' || notifPref === 'both') && !user?.phoneNumber && (
            <p className="text-xs text-amber-600 mt-1">Add a phone number above to receive SMS notifications.</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="optIn" className="w-4 h-4 text-green-600 rounded"
            checked={optIn} onChange={e => setOptIn(e.target.checked)} />
          <label htmlFor="optIn" className="text-sm text-gray-700">
            Notify me about new events in my locations
          </label>
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}
        {message && <p className="text-green-600 text-sm">{message}</p>}

        <button
          type="submit"
          disabled={saving}
          className="w-full py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </form>

      {/* Role upgrade request */}
      {user && isEligibleToRequest(user) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Request Role Upgrade</h2>
          <p className="text-sm text-gray-500 mb-4">
            Apply to become a Teacher or Organizer. Yoga Alliance-certified teachers with a public profile are approved with their credentials reflected on their teacher page. Other applicants are reviewed manually — include as much detail as possible in your note. The Organizer role is for coordinators who manage classes on behalf of a teacher but do not personally lead sessions.
          </p>

          {pendingRequests.length > 0 && (
            <div className="mb-4 space-y-2">
              {pendingRequests.map(req => (
                <div key={req.requestId} className="flex items-center gap-2 text-sm bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                  <span className="text-yellow-600 font-medium">{req.targetRole}</span>
                  <span className="text-yellow-500">request pending review</span>
                  <span className="ml-auto text-xs text-gray-400">
                    {new Date(req.createdAt).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleRequestSubmit} className="space-y-3">
            <div>
              <label className={label}>Requested Role</label>
              <select
                className={input}
                value={reqTargetRole}
                onChange={e => setReqTargetRole(e.target.value as RequestableRole)}
              >
                <option value="Teacher">Teacher — teach and lead yoga sessions</option>
                <option value="Organizer">Organizer — create and manage events</option>
              </select>
            </div>

            {reqTargetRole === 'Teacher' && (
              <div>
                <label className={label}>
                  Yoga Alliance Profile URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  className={input}
                  placeholder="https://app.yogaalliance.org/teacherpublicprofile?id=..."
                  value={reqYaUrl}
                  onChange={e => setReqYaUrl(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">
                  Find your profile at{' '}
                  <a href="https://www.yogaalliance.org/directory" target="_blank" rel="noreferrer" className="underline">
                    yogaalliance.org/directory
                  </a>. Paste the full URL here. Required to verify your teaching credentials.
                </p>
              </div>
            )}

            <div>
              <label className={label}>Why are you requesting this role?</label>
              <textarea
                className={`${input} resize-none`}
                rows={3}
                placeholder="Tell us about your background and why you'd like this role (min 10 characters)"
                value={reqReason}
                onChange={e => setReqReason(e.target.value)}
              />
            </div>

            {reqMessage && (
              <p className={`text-sm ${reqMessage.ok ? 'text-green-600' : 'text-red-500'}`}>
                {reqMessage.text}
              </p>
            )}

            <button
              type="submit"
              disabled={submittingReq}
              className="w-full py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submittingReq ? 'Submitting…' : 'Submit Request'}
            </button>
          </form>
        </div>
      )}

      {/* Teacher profile */}
      {user?.roles.includes('Teacher') && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-900">Teacher Profile</h2>
            {teacherProfile && (
              <Link to={`/teachers/${user.username ?? user.userId}`} className="text-sm text-primary-600 hover:underline">
                View public profile →
              </Link>
            )}
          </div>

          {teacherProfile && (
            <p className="text-sm text-gray-500 mb-1">
              Verification status:{' '}
              <span className={`font-medium ${verificationLabel(teacherProfile.verificationStatus).cls}`}>
                {verificationLabel(teacherProfile.verificationStatus).text}
              </span>
            </p>
          )}

          <form onSubmit={handleTeacherSave} className="space-y-4 mt-4">
            <div>
              <label className={label}>Bio</label>
              <textarea
                className={`${input} resize-none`}
                rows={4}
                maxLength={2000}
                placeholder="Tell students about your yoga background and teaching style"
                value={ldrBio}
                onChange={e => setLdrBio(e.target.value)}
              />
            </div>

            <div>
              <label className={label}>Years of Experience</label>
              <input
                type="number"
                min={0}
                max={70}
                className={input}
                placeholder="e.g. 5"
                value={ldrYears}
                onChange={e => setLdrYears(e.target.value)}
              />
            </div>

            <div>
              <label className={label}>Certifications</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {CERTIFICATIONS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleItem(ldrCerts, setLdrCerts, c)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      ldrCerts.includes(c)
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={label}>Specialties</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {SPECIALTIES.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleItem(ldrSpecialties, setLdrSpecialties, s)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      ldrSpecialties.includes(s)
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-green-400'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={label}>Yoga Alliance Profile URL <span className="font-normal text-gray-400">(optional)</span></label>
              <input
                type="url"
                className={input}
                placeholder="https://app.yogaalliance.org/teacherpublicprofile?id=..."
                value={ldrYaUrl ?? ''}
                onChange={e => setLdrYaUrl(e.target.value)}
              />
              <p className="text-xs text-gray-400 mt-1">
                Used to verify your credentials. Find your profile at{' '}
                <a href="https://www.yogaalliance.org/directory" target="_blank" rel="noreferrer" className="underline">
                  yogaalliance.org/directory
                </a>.
              </p>
            </div>

            {ldrMessage && (
              <p className={`text-sm ${ldrMessage.ok ? 'text-green-600' : 'text-red-500'}`}>
                {ldrMessage.text}
              </p>
            )}

            <button
              type="submit"
              disabled={ldrSaving}
              className="w-full py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {ldrSaving ? 'Saving…' : 'Save Teacher Profile'}
            </button>
          </form>
        </div>
      )}

      {/* Contact verification modal */}
      {showVerifyModal && pendingVerifyType && (
        <ContactVerificationModal
          type={pendingVerifyType}
          newValue={pendingVerifyValue}
          onClose={() => setShowVerifyModal(false)}
          onVerified={handleVerified}
        />
      )}
    </div>
  );
};
